import React, { useState, useEffect, FormEvent } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "../../components/layout/PublicLayout";
import { useAuth } from "../../context/AuthContext";
import { applyAsInvestor } from "../../services/investorApi";
import { InvestorApplication } from "../../types/investor";
import { GoogleSignInButton, AuthDivider } from "../../components/auth";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5555";

interface FirebaseError extends Error {
  code?: string;
}

const BecomeInvestorPage: React.FC = () => {
  const { currentUser, login, signup, resetPassword } = useAuth();
  // Investor status state
  const [isApprovedInvestor, setIsApprovedInvestor] = useState(false);
  const [userPromoCode, setUserPromoCode] = useState<string | null>(null);
  const [checkingStatus, setCheckingStatus] = useState(false);

  // Check if user is already an approved investor with promo
  useEffect(() => {
    const checkInvestorStatus = async () => {
      if (!currentUser) {
        setIsApprovedInvestor(false);
        setUserPromoCode(null);
        return;
      }

      setCheckingStatus(true);
      try {
        // Get investor data from referrals endpoint (promo code is in investors collection)
        const response = await fetch(
          `${API_BASE}/api/users/${currentUser.uid}/referrals`
        );
        if (response.ok) {
          const data = await response.json();
          if (data.promoCode) {
            setIsApprovedInvestor(true);
            setUserPromoCode(data.promoCode);
          } else {
            setIsApprovedInvestor(false);
            setUserPromoCode(null);
          }
        } else {
          setIsApprovedInvestor(false);
          setUserPromoCode(null);
        }
      } catch {
        setIsApprovedInvestor(false);
        setUserPromoCode(null);
      }
      setCheckingStatus(false);
    };

    checkInvestorStatus();
  }, [currentUser]);

  // Application form state
  const [formData, setFormData] = useState<InvestorApplication>({
    name: "",
    requestedPromoCode: "",
    reason: "",
    website: "",
    expectedReferrals: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Auth form state
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetMessage, setResetMessage] = useState("");

  const handleApplicationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentUser) {
      return;
    }

    if (!formData.name.trim()) {
      setError("Please enter your name");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await applyAsInvestor(currentUser.uid, currentUser.email || "", formData);
      setSuccess(true);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to submit application"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAuthSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setAuthError("");
    setResetMessage("");

    if (!isLogin && password !== confirmPassword) {
      setAuthError("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      setAuthError("Password must be at least 6 characters");
      return;
    }

    setAuthLoading(true);

    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await signup(email, password);
      }
      // User is now logged in, the form will automatically switch to application form
    } catch (err) {
      const firebaseError = err as FirebaseError;
      if (firebaseError.code === "auth/user-not-found") {
        setAuthError("No account found with this email");
      } else if (firebaseError.code === "auth/wrong-password") {
        setAuthError("Incorrect password");
      } else if (firebaseError.code === "auth/invalid-credential") {
        setAuthError("Invalid email or password");
      } else if (firebaseError.code === "auth/email-already-in-use") {
        setAuthError("Email already in use");
      } else if (firebaseError.code === "auth/invalid-email") {
        setAuthError("Invalid email address");
      } else if (firebaseError.code === "auth/weak-password") {
        setAuthError("Password is too weak");
      } else if (firebaseError.code === "auth/too-many-requests") {
        setAuthError("Too many failed attempts. Please try again later.");
      } else {
        setAuthError("Failed to " + (isLogin ? "log in" : "sign up"));
      }
    }

    setAuthLoading(false);
  };

  const handleForgotPassword = async (e: FormEvent) => {
    e.preventDefault();
    setAuthError("");
    setResetMessage("");

    if (!email) {
      setAuthError("Please enter your email address");
      return;
    }

    setAuthLoading(true);

    try {
      await resetPassword(email);
      setResetMessage("Password reset email sent! Check your inbox.");
      setShowForgotPassword(false);
    } catch (err) {
      const firebaseError = err as FirebaseError;
      if (firebaseError.code === "auth/user-not-found") {
        setAuthError("No account found with this email");
      } else {
        setAuthError("Failed to send reset email");
      }
    }

    setAuthLoading(false);
  };

  const handleGoogleError = (errorMessage: string) => {
    setAuthError(errorMessage);
  };

  if (success) {
    return (
      <PublicLayout>
        <div className="py-16 sm:py-24">
          <div className="max-w-2xl mx-auto px-4 text-center">
            <div className="bg-green-50 border border-green-200 rounded-2xl p-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Application Submitted!
              </h2>
              <p className="text-gray-600 mb-6">
                Thank you for your interest in becoming a Litrlly investor.
                We'll review your application and get back to you within 24-48
                hours.
              </p>
              <Link
                to="/app"
                className="inline-flex items-center px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors"
              >
                Return to App
              </Link>
            </div>
          </div>
        </div>
      </PublicLayout>
    );
  }

  // Render auth form content
  const renderAuthForm = () => (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          {showForgotPassword
            ? "Reset Password"
            : isLogin
            ? "Sign in to apply"
            : "Create an account"}
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          {showForgotPassword
            ? "Enter your email to receive a reset link"
            : isLogin
            ? "Don't have an account? "
            : "Already have an account? "}
          {!showForgotPassword && (
            <button
              type="button"
              onClick={() => {
                setIsLogin(!isLogin);
                setAuthError("");
              }}
              className="text-purple-600 hover:text-purple-700 font-medium"
            >
              {isLogin ? "Sign up" : "Sign in"}
            </button>
          )}
        </p>
      </div>

      {authError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {authError}
        </div>
      )}

      {resetMessage && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          {resetMessage}
        </div>
      )}

      {showForgotPassword ? (
        <form onSubmit={handleForgotPassword} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 bg-white text-gray-900 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
              placeholder="Enter your email"
              required
            />
          </div>
          <button
            type="submit"
            disabled={authLoading}
            className="w-full py-3 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
          >
            {authLoading ? "Sending..." : "Send Reset Link"}
          </button>
          <button
            type="button"
            onClick={() => setShowForgotPassword(false)}
            className="w-full py-2 text-gray-600 hover:text-gray-800 text-sm"
          >
            Back to Sign In
          </button>
        </form>
      ) : (
        <form onSubmit={handleAuthSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 bg-white text-gray-900 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
              placeholder="Enter your email"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 bg-white text-gray-900 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
              placeholder="Enter your password"
              required
            />
          </div>

          {!isLogin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 bg-white text-gray-900 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                placeholder="Confirm your password"
                required
              />
            </div>
          )}

          {isLogin && (
            <div className="text-right">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-purple-600 hover:text-purple-700"
              >
                Forgot Password?
              </button>
            </div>
          )}

          <button
            type="submit"
            disabled={authLoading}
            className="w-full py-3 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
          >
            {authLoading
              ? "Please wait..."
              : isLogin
              ? "Sign In"
              : "Create Account"}
          </button>

          <AuthDivider />

          <GoogleSignInButton onError={handleGoogleError} />
        </form>
      )}
    </div>
  );

  // Render approved investor view
  const renderApprovedInvestor = () => (
    <div className="text-center">
      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <svg
          className="w-8 h-8 text-green-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 13l4 4L19 7"
          />
        </svg>
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-2">
        You're an Approved Investor!
      </h3>
      <p className="text-gray-600 mb-6">
        Your promo code is ready to share with your audience.
      </p>
      <div className="bg-purple-50 border border-purple-200 rounded-xl p-6 mb-6">
        <p className="text-sm text-purple-600 mb-2">Your Promo Code</p>
        <p className="text-3xl font-bold text-purple-700">{userPromoCode}</p>
      </div>
      <Link
        to="/investor"
        className="inline-flex items-center px-6 py-3 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors"
      >
        Go to Investor Dashboard
      </Link>
    </div>
  );

  // Render application form content
  const renderApplicationForm = () => (
    <form onSubmit={handleApplicationSubmit} className="space-y-4">
      <div className="flex items-center gap-3 p-3 bg-purple-50 border border-purple-200 rounded-lg mb-4">
        <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
          <svg
            className="w-4 h-4 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <div>
          <p className="text-sm font-medium text-purple-900">Signed in as</p>
          <p className="text-sm text-purple-700">{currentUser?.email}</p>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Your Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="w-full px-4 py-2 border border-gray-300 bg-white text-gray-900 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
          placeholder="John Doe"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Preferred Promo Code{" "}
          <span className="text-gray-400 font-normal">(optional)</span>
        </label>
        <input
          type="text"
          value={formData.requestedPromoCode}
          onChange={(e) =>
            setFormData({
              ...formData,
              requestedPromoCode: e.target.value.toUpperCase(),
            })
          }
          className="w-full px-4 py-2 border border-gray-300 bg-white text-gray-900 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
          placeholder="MYCODE"
          maxLength={15}
        />
        <p className="text-xs text-gray-500 mt-1">
          Leave empty to have one generated for you
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Website / Social Media{" "}
          <span className="text-gray-400 font-normal">(optional)</span>
        </label>
        <input
          type="text"
          value={formData.website}
          onChange={(e) =>
            setFormData({ ...formData, website: e.target.value })
          }
          className="w-full px-4 py-2 border border-gray-300 bg-white text-gray-900 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
          placeholder="https://yourwebsite.com or @yoursocial"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Why do you want to become an investor?
        </label>
        <textarea
          value={formData.reason}
          onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
          className="w-full px-4 py-2 border border-gray-300 bg-white text-gray-900 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none resize-none"
          rows={3}
          placeholder="Tell us about yourself and how you plan to promote Litrlly..."
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Expected Monthly Referrals
        </label>
        <select
          value={formData.expectedReferrals}
          onChange={(e) =>
            setFormData({ ...formData, expectedReferrals: e.target.value })
          }
          className="w-full px-4 py-2 border border-gray-300 bg-white text-gray-900 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
        >
          <option value="">Select...</option>
          <option value="1-10">1-10 users</option>
          <option value="10-50">10-50 users</option>
          <option value="50-100">50-100 users</option>
          <option value="100+">100+ users</option>
        </select>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full py-3 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? "Submitting..." : "Submit Application"}
      </button>

      <p className="text-xs text-gray-500 text-center">
        By applying, you agree to our{" "}
        <Link to="/terms" className="text-purple-600 hover:underline">
          Terms of Service
        </Link>
        .
      </p>
    </form>
  );

  return (
    <PublicLayout>
      <div className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold mb-4">
              Partner Program
            </span>
            <h1 className="text-4xl font-extrabold text-gray-900 sm:text-5xl mb-6">
              Earn 20% Commission
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Join our investor program and earn passive income by referring new
              users to Litrlly. Get paid 20% of every subscription from users
              you refer.
            </p>
          </div>

          {/* How It Works */}
          <div className="mb-20">
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-12">
              How It Works
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-purple-600">1</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Apply & Get Approved
                </h3>
                <p className="text-gray-600">
                  Fill out the application form below. Once approved, you'll
                  receive your unique referral code.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-purple-600">2</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Share Your Code
                </h3>
                <p className="text-gray-600">
                  Share your referral link or code with your audience. Track
                  signups in your dashboard.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-purple-600">3</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Earn Commissions
                </h3>
                <p className="text-gray-600">
                  Earn 20% commission on every subscription payment from users
                  you referred. Recurring!
                </p>
              </div>
            </div>
          </div>

          {/* Benefits */}
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-3xl p-8 md:p-12 mb-20">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              Investor Benefits
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    20% Recurring Commission
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Earn 20% of every subscription payment, not just the first
                    one.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Real-time Tracking
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Track your referrals and earnings through the admin panel.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Fast Payouts
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Request payouts anytime once you reach the $50 minimum
                    threshold.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Dedicated Support
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Get personalized support and resources to help you succeed.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Commission Example */}
          <div className="bg-white border border-gray-200 rounded-2xl p-8 mb-20">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              Earnings Example
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-gray-600 font-medium">
                      Scenario
                    </th>
                    <th className="text-right py-3 px-4 text-gray-600 font-medium">
                      Referrals
                    </th>
                    <th className="text-right py-3 px-4 text-gray-600 font-medium">
                      Avg. Plan
                    </th>
                    <th className="text-right py-3 px-4 text-gray-600 font-medium">
                      Monthly Earnings
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100">
                    <td className="py-3 px-4 text-gray-900">Starter</td>
                    <td className="text-right py-3 px-4 text-gray-600">
                      10 users
                    </td>
                    <td className="text-right py-3 px-4 text-gray-600">
                      $9.99/mo
                    </td>
                    <td className="text-right py-3 px-4 text-green-600 font-semibold">
                      $19.98/mo
                    </td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-3 px-4 text-gray-900">Growing</td>
                    <td className="text-right py-3 px-4 text-gray-600">
                      50 users
                    </td>
                    <td className="text-right py-3 px-4 text-gray-600">
                      $9.99/mo
                    </td>
                    <td className="text-right py-3 px-4 text-green-600 font-semibold">
                      $99.90/mo
                    </td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 text-gray-900">Established</td>
                    <td className="text-right py-3 px-4 text-gray-600">
                      200 users
                    </td>
                    <td className="text-right py-3 px-4 text-gray-600">
                      $9.99/mo
                    </td>
                    <td className="text-right py-3 px-4 text-green-600 font-semibold">
                      $399.60/mo
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-center text-sm text-gray-500 mt-4">
              * Based on 20% commission rate. Actual earnings depend on
              subscription plans and user retention.
            </p>
          </div>

          {/* Application Form */}
          <div className="max-w-xl mx-auto">
            <div className="bg-white border border-gray-200 rounded-2xl p-8">
              {checkingStatus ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                  <p className="text-gray-500 mt-4">Checking status...</p>
                </div>
              ) : isApprovedInvestor ? (
                renderApprovedInvestor()
              ) : (
                <>
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                    Apply Now
                  </h2>
                  {currentUser ? renderApplicationForm() : renderAuthForm()}
                </>
              )}
            </div>
          </div>

          {/* FAQ */}
          <div className="mt-20">
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">
              Frequently Asked Questions
            </h2>
            <div className="max-w-3xl mx-auto space-y-4">
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">
                  How long does approval take?
                </h3>
                <p className="text-gray-600 text-sm">
                  Most applications are reviewed within 24-48 hours. You'll
                  receive an email once approved.
                </p>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">
                  When can I request a payout?
                </h3>
                <p className="text-gray-600 text-sm">
                  You can request a payout anytime your balance reaches $50 or
                  more. Payouts are processed via PayPal or bank transfer.
                </p>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">
                  Is the commission recurring?
                </h3>
                <p className="text-gray-600 text-sm">
                  Yes! You earn 20% commission on every subscription payment
                  from users you referred, as long as they remain subscribed.
                </p>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">
                  Can I see who I've referred?
                </h3>
                <p className="text-gray-600 text-sm">
                  Once approved, you'll be able to track your referrals and
                  earnings. Contact us for detailed reports.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default BecomeInvestorPage;
